/**
 * Clean Menu Renderer & Category Filter Component
 * Editorial Menu Layout: Featured "Chef's Masterpiece" Hero Banner (Top)
 * and Crisp Uniform Grid Below with interactive dish modal playback.
 */

import menuData from '../data/menu.json';
import { gsap } from 'gsap';

export function initMenu(containerSelector = '#menu-container') {
  const container = document.querySelector(containerSelector);
  if (!container) return;

  let activeCategory = 'All Specialties';
  let jainOnly = false;

  const categories = [
    'All Specialties',
    ...new Set(menuData.items.map(i => i.category))
  ];

  // Create Modal DOM Backdrop once
  let modalBackdrop = document.querySelector('.menu-modal-backdrop');
  if (!modalBackdrop) {
    modalBackdrop = document.createElement('div');
    modalBackdrop.className = 'menu-modal-backdrop';
    modalBackdrop.setAttribute('role', 'dialog');
    modalBackdrop.setAttribute('aria-modal', 'true');
    modalBackdrop.setAttribute('aria-hidden', 'true');
    document.body.appendChild(modalBackdrop);

    modalBackdrop.addEventListener('click', (e) => {
      if (e.target === modalBackdrop) closeDishModal();
    });

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modalBackdrop.classList.contains('active')) {
        closeDishModal();
      }
    });
  }

  let originalDocTitle = document.title;

  function openDishModal(item) {
    if (!originalDocTitle) originalDocTitle = document.title;
    document.title = `${item.name} (₹${item.price}) | Majhya Kolhapur Sathi Menu`;
    const hasVideo = Boolean(item.hasVideo || item.video);
    const mediaHtml = hasVideo ? `
      <div class="modal-media-frame">
        <video autoplay muted loop playsinline poster="/${item.image || 'assets/optimized/hero-poster.jpg'}">
          <source src="/${item.video || 'assets/optimized/rassa-pour-hero.mp4'}" type="video/mp4">
          Your browser does not support HTML5 video.
        </video>
      </div>
    ` : (item.image ? `
      <div class="modal-media-frame">
        <img src="/${item.image}" alt="${item.name}">
      </div>
    ` : '');

    modalBackdrop.innerHTML = `
      <div class="menu-modal-content">
        <div class="modal-drag-handle" aria-hidden="true"></div>
        <button class="menu-modal-close" aria-label="Close modal">&times;</button>
        ${mediaHtml}
        <div class="modal-title-row">
          <div>
            <h3 class="modal-title">${item.name}</h3>
            ${item.marathiName ? `<span class="dish-marathi">${item.marathiName}</span>` : ''}
          </div>
          <span class="modal-price">₹${item.price}</span>
        </div>
        <p class="modal-desc">${item.description}</p>
        ${item.includes && item.includes.length > 0 ? `
          <div class="dish-includes">
            <strong>Includes on Plate:</strong> ${item.includes.join(' • ')}
          </div>
        ` : ''}
        <div class="modal-actions">
          <a href="tel:${menuData.restaurant.phone}" class="btn btn-chili" style="flex: 1;">Call to Order (₹${item.price})</a>
          <a href="${menuData.restaurant.orderingPlatforms[0].url}" target="_blank" rel="noopener noreferrer" class="btn btn-outline" style="flex: 1;">Order Online</a>
        </div>
      </div>
    `;

    modalBackdrop.querySelector('.menu-modal-close')?.addEventListener('click', closeDishModal);

    // Mobile swipe-down gesture to dismiss bottom sheet
    const modalContent = modalBackdrop.querySelector('.menu-modal-content');
    if (modalContent) {
      let touchStartY = 0;
      modalContent.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
      }, { passive: true });

      modalContent.addEventListener('touchmove', (e) => {
        const touchCurrentY = e.touches[0].clientY;
        const diffY = touchCurrentY - touchStartY;
        // When swiping down at top of sheet
        if (diffY > 65 && modalContent.scrollTop <= 0) {
          closeDishModal();
        }
      }, { passive: true });
    }

    modalBackdrop.classList.add('active');
    modalBackdrop.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeDishModal() {
    if (originalDocTitle) document.title = originalDocTitle;
    modalBackdrop.classList.remove('active');
    modalBackdrop.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    const video = modalBackdrop.querySelector('video');
    if (video) video.pause();
  }

  function render() {
    // Filter items based on category and Jain toggle
    const filteredItems = menuData.items.filter(item => {
      const matchesCategory = activeCategory === 'All Specialties' || item.category === activeCategory;
      const matchesJain = !jainOnly || item.isJain;
      return matchesCategory && matchesJain;
    });

    const featured = filteredItems[0]; // Spotlight the first item as the Chef's Masterpiece
    const rest = filteredItems.slice(1);

    container.innerHTML = `
      <!-- Category Filter Controls -->
      <div class="menu-controls">
        <div class="menu-category-pills" role="tablist" aria-label="Menu Categories">
          ${categories.map(cat => `
            <button 
              type="button" 
              class="menu-pill ${activeCategory === cat ? 'active' : ''}" 
              data-category="${cat}"
              role="tab"
              aria-selected="${activeCategory === cat}"
            >
              <span>${cat}</span>
            </button>
          `).join('')}
        </div>

        <div class="menu-filter-bar">
          <span>Dietary Preference:</span>
          <button type="button" class="jain-toggle-btn ${jainOnly ? 'active' : ''}" id="jain-toggle">
            <span>✨ Jain Options Only (No Onion / Garlic)</span>
          </button>
        </div>
      </div>

      ${filteredItems.length === 0 ? `
        <div style="text-align: center; padding: 48px; background: #ffffff; border-radius: 16px; border: 1px dashed var(--color-border, #e8dfd3); margin-top: 2rem;">
          <p style="font-size: 1.125rem; font-weight: 600; color: #555555;">No dishes match the selected filter.</p>
          <button type="button" class="btn btn-outline" id="reset-filter-btn" style="margin-top: 16px;">View All Dishes</button>
        </div>
      ` : `
        <!-- Featured / Latest Story Style Card (Top Banner) -->
        ${featured ? `
          <div class="menu-featured-card" data-id="${featured.id}">
            <div class="menu-featured-image" data-action="open-modal" role="button" tabindex="0" aria-label="View prep & details for ${featured.name}">
              <img src="/${featured.image}" alt="${featured.name}" loading="lazy">
              <span class="featured-specialty-badge">★ Featured Specialty</span>
              ${(featured.hasVideo || featured.video) ? `<span class="video-badge featured-video-badge" data-action="open-modal">▶ Watch Prep</span>` : ''}
            </div>
            <div class="menu-featured-content">
              <span class="featured-marathi">${featured.marathiName || ''}</span>
              <div class="featured-header-row">
                <h3 class="featured-title">${featured.name}</h3>
                <span class="featured-price">₹${featured.price}</span>
              </div>
              <p class="featured-desc">${featured.description}</p>
              ${featured.includes && featured.includes.length > 0 ? `
                <div class="dish-includes">
                  <strong>Includes on Plate:</strong> ${featured.includes.join(' • ')}
                </div>
              ` : ''}
              <div class="featured-actions-row">
                <button type="button" class="btn-primary open-dish-btn btn-sweep" data-action="open-modal">View Details & Prep</button>
                <div class="dish-badges-footer">
                  <span class="badge badge-veg"><span class="veg-mark"><span class="veg-mark-dot"></span></span>Pure Veg</span>
                  ${featured.isSignature ? `<span class="badge badge-signature">★ Specialty</span>` : ''}
                  ${featured.isJain ? `<span class="badge badge-jain">Jain Available</span>` : ''}
                </div>
              </div>
            </div>
          </div>
        ` : ''}

        <!-- Standard Grid for Other Dishes (2-Column on Mobile) -->
        <div class="menu-grid">
          ${rest.map(item => `
            <article class="menu-card dish-card" data-id="${item.id}">
              <div class="dish-image-wrapper" data-action="open-modal" role="button" tabindex="0" aria-label="View details for ${item.name}">
                <img src="/${item.image}" alt="${item.name}" loading="lazy">
                ${(item.hasVideo || item.video) ? `<span class="video-badge" data-action="open-modal">▶ Watch Prep</span>` : ''}
              </div>
              <div class="dish-content">
                <div class="dish-header-row">
                  <div class="dish-title-group">
                    <h3 class="dish-title">${item.name}</h3>
                    ${item.marathiName ? `<span class="dish-marathi">${item.marathiName}</span>` : ''}
                  </div>
                  <span class="dish-price">₹${item.price}</span>
                </div>
                <p class="dish-description">${item.description}</p>
                ${item.includes && item.includes.length > 0 ? `
                  <div class="dish-includes">
                    <strong>Includes:</strong> ${item.includes.join(' • ')}
                  </div>
                ` : ''}
                <div class="dish-footer-row">
                  <div class="dish-badges-footer">
                    <span class="badge badge-veg"><span class="veg-mark"><span class="veg-mark-dot"></span></span>Pure Veg</span>
                    ${item.isSignature ? `<span class="badge badge-signature">★ Specialty</span>` : ''}
                    ${item.isJain ? `<span class="badge badge-jain">Jain</span>` : ''}
                    ${item.isUpvas ? `<span class="badge badge-upvas">Upvas</span>` : ''}
                  </div>
                  <button type="button" class="dish-view-btn" data-action="open-modal" aria-label="View ${item.name} details">
                    <span>Details</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </button>
                </div>
              </div>
            </article>
          `).join('')}
        </div>
      `}

      <!-- Accompaniments & Extras Box -->
      ${renderExtrasBox()}

      <!-- Delivery / Dine-in Prompt -->
      <div class="menu-bottom-callout">
        <span class="section-tag" style="background:var(--color-chili-tint, #fdf1ef); color:var(--color-chili-red, #b8321a); border-color:rgba(184,46,31,0.25);">घरपोच किंवा पार्सल</span>
        <h3 class="callout-title">Craving Authentic Kolhapuri Flavors?</h3>
        <p class="callout-desc">
          Dine in with us opposite Marathon Futurex, pick up a fresh hot takeaway, or order directly to your desk or home via our verified delivery partners.
        </p>
        <div class="callout-actions-row">
          <a href="tel:${menuData.restaurant.phone}" class="callout-action-btn callout-call-btn">
            <span class="callout-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
            </span>
            <span class="callout-text-group">
              <span class="callout-primary-text">Call for Takeaway</span>
              <span class="callout-sub-text">+91 98335 35454</span>
            </span>
          </a>

          <a href="${menuData.restaurant.orderingPlatforms[0].url}" target="_blank" rel="noopener noreferrer" class="callout-action-btn callout-zomato-btn">
            <span class="callout-badge-circle zomato-circle" aria-hidden="true">Z</span>
            <span class="callout-text-group">
              <span class="callout-primary-text">Order on Zomato</span>
              <span class="callout-sub-text">★ 4.1 Rating • Delivery</span>
            </span>
            <span class="callout-arrow" aria-hidden="true">→</span>
          </a>

          <a href="${menuData.restaurant.orderingPlatforms[1].url}" target="_blank" rel="noopener noreferrer" class="callout-action-btn callout-swiggy-btn">
            <span class="callout-badge-circle swiggy-circle" aria-hidden="true">S</span>
            <span class="callout-text-group">
              <span class="callout-primary-text">Order on Swiggy</span>
              <span class="callout-sub-text">Live Tracking • Fast</span>
            </span>
            <span class="callout-arrow" aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    `;

    // Smooth staggered entrance animation without stiffness & clearProps to guarantee no dimming
    gsap.from(".menu-featured-card, .menu-card", {
      opacity: 0,
      y: 30,
      duration: 0.5,
      stagger: 0.08,
      ease: "power2.out",
      clearProps: "opacity,transform"
    });

    attachEventListeners();
  }

  function getExtraIcon(id) {
    if (id.includes('water')) return '💧';
    if (id.includes('butter-milk') || id.includes('chaas')) return '🥛';
    if (id.includes('dahi')) return '🥣';
    if (id.includes('bhakri')) return '🫓';
    if (id.includes('pav')) return '🥖';
    if (id.includes('thecha')) return '🌶️';
    if (id.includes('sheera')) return '🍯';
    if (id.includes('farsan')) return '🥨';
    if (id.includes('matki')) return '🌱';
    if (id.includes('aloo')) return '🥔';
    if (id.includes('pitla')) return '🍲';
    if (id.includes('bhaji')) return '🥘';
    return '🥣';
  }

  function formatExtraName(fullName) {
    const match = fullName.match(/^(.*?)\s*\((.*?)\)$/);
    if (match) {
      return `
        <div class="extra-name-wrap">
          <span class="extra-en">${match[1].trim()}</span>
          <span class="extra-mr">${match[2].trim()}</span>
        </div>
      `;
    }
    return `
      <div class="extra-name-wrap">
        <span class="extra-en">${fullName}</span>
      </div>
    `;
  }

  function renderExtrasBox() {
    const extrasCategory = menuData.categories.find(c => c.id === 'extras');
    if (!extrasCategory) return '';

    return `
      <div class="menu-extras-container">
        <div class="extras-header">
          <span class="section-tag" style="background:var(--color-turmeric-tint); color:var(--color-turmeric-dark); border-color:rgba(217,130,43,0.25);">जादा वाटी व पदार्थ</span>
          <h3 class="extras-title">Accompaniments & Fresh Extras</h3>
          <p class="extras-subtitle">
            Customize your misal thali or lunch plate with freshly prepared extra portions.
          </p>
        </div>
        <div class="extras-grid">
          ${extrasCategory.items.map(extra => `
            <div class="extra-item-card">
              <div class="extra-item-left">
                <span class="extra-item-icon" aria-hidden="true">${getExtraIcon(extra.id)}</span>
                ${formatExtraName(extra.name)}
              </div>
              <span class="extra-item-price-pill">₹${extra.price}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  function attachEventListeners() {
    // Category pills click
    container.querySelectorAll('.menu-pill').forEach(btn => {
      btn.addEventListener('click', () => {
        activeCategory = btn.dataset.category;
        render();
        // Auto-center active pill with smooth scroll
        setTimeout(() => {
          const activeBtn = container.querySelector(`.menu-pill[data-category="${activeCategory}"]`);
          activeBtn?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }, 50);
      });
    });

    // Jain filter toggle
    const jainBtn = container.querySelector('#jain-toggle');
    jainBtn?.addEventListener('click', () => {
      jainOnly = !jainOnly;
      render();
    });

    // Reset filter button
    const resetBtn = container.querySelector('#reset-filter-btn');
    resetBtn?.addEventListener('click', () => {
      activeCategory = 'All Specialties';
      jainOnly = false;
      render();
    });

    // Only specific buttons or image clicks open the dish detail/video modal (NOT whole card background, ensuring smooth scrolling)
    container.querySelectorAll('[data-action="open-modal"]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = trigger.closest('[data-id]');
        if (!card) return;
        const dishId = card.dataset.id;
        const selectedDish = menuData.items.find(d => d.id === dishId);
        if (selectedDish) {
          openDishModal(selectedDish);
        }
      });

      if (trigger.getAttribute('role') === 'button') {
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            trigger.click();
          }
        });
      }
    });

    initFeaturedBannerParallax();
  }

  // Initial render
  render();
}

/**
 * Featured Banner Parallax & Cursor Hover Tilt Interaction
 */
export function initFeaturedBannerParallax() {
  const featuredCard = document.querySelector(".menu-featured-card");
  const featuredImage = document.querySelector(".menu-featured-image img");

  if (!featuredCard || !featuredImage) return;

  featuredCard.addEventListener("mousemove", (e) => {
    const rect = featuredCard.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;

    // Subtle translation for 3D depth + scale up
    featuredImage.style.transform = `scale(1.08) translate(${x * 0.03}px, ${y * 0.03}px)`;
  });

  featuredCard.addEventListener("mouseleave", () => {
    // Smooth reset on mouse leave
    featuredImage.style.transform = "scale(1) translate(0px, 0px)";
  });
}
